import React, { ComponentProps, FunctionComponent, useMemo } from 'react';

import TracPage from '@services/tracpage/types/trac-page';

import useIsMobile from '@shared-tracpage/hooks/is-mobile';

import {
  SidebarHighlight,
  SidebarWrapper,
  SocialLinks,
  SocialLinksContent,
  StreamsWrapper,
  Wrapper,
} from '@shared-tracpage/components/sidebar';
import { ArtistInfo } from '@shared-tracpage/components/artist-info';
import { SocialLink } from '@shared-tracpage/components/social-link';
import StatusIcon from '@shared-tracpage/icons/status-icon';
import { SidebarFooter } from '@shared-tracpage/components/sidebar/sidebar-footer';
import { SidebarFeaturedItem } from '@shared-tracpage/components/sidebar/sidebar-featured-item';
import TracpageNavbar from '../navbar';
import SimpleArtist from '@common/types/artist/simple-artist';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';
import { TracPageFeatureItemType } from '@services/tracpage/types/trac-page-feature-item';
import { NewTourEvent } from '@common/types/traclink/new-tour-event';
import { MerchProduct } from '@services/merch/types/api/merch-product';
import { getSocialIcon } from '@shared-tracpage/utils/get-social-icon';
import { MonthAnalytics } from '@webapp/store/artist-analytics/state';

type StateProps = {
  artist: SimpleArtist | null;
  events: NewTourEvent[];
  merch: MerchProduct[];
  lastMonthAnalytics: MonthAnalytics;
};
type Props = {
  className?: string;
  pageData: TracPage;
  analytics?: never;
  hideIcons?: boolean;
  onCartClick?: () => void;
  featuredItem?: never;
} & StateProps;

const mapStateToProps = (state: AppState): StateProps => ({
  artist:
    state.artist.myArtists.find((a) => a.id === state.activeProfile.id) ?? null,
  events: [
    ...state.traclink.tourEventDrafts.new,
    ...state.traclink.tourEvents.map(
      (te) => state.traclink.tourEventDrafts.edit[te.id] ?? te
    ),
  ],
  merch: state.merch.artistMerch,
  lastMonthAnalytics: state.artistAnalytics.lastMonthAnalytics,
});

const Sidebar: FunctionComponent<Props> = ({
  className,
  pageData,
  hideIcons,
  artist,
  events,
  merch,
  lastMonthAnalytics,
}) => {
  const isMobile = useIsMobile();

  const SocialItems = pageData.data.social.filter(
    (s) => s.url && s.url !== 'NEW'
  );

  const stats = useMemo(() => {
    const { change: streams, percentChange } = lastMonthAnalytics;
    const status =
      percentChange === 0 ? 'equal' : percentChange > 0 ? 'up' : 'down';

    return {
      streams,
      percentageChange: `${Math.round(percentChange)}%`,
      status,
    };
  }, [lastMonthAnalytics]);

  const featuredItem = useMemo(() => {
    switch (pageData.featuredItem?.type) {
      case TracPageFeatureItemType.MusicRelease:
        return null;
      case TracPageFeatureItemType.MerchProduct:
        return merch.find((m) => m.id === pageData.featuredItem?.id) ?? null;
      case TracPageFeatureItemType.TourEvent:
        return events.find((e) => e.id === pageData.featuredItem?.id) ?? null;
      case TracPageFeatureItemType.TracLink:
        return (
          pageData.links.find(
            (link) => link.id === pageData.featuredItem?.id
          ) ?? null
        );
      default:
        return null;
    }
  }, [pageData, events]);

  return (
    <SidebarWrapper className={className || ''}>
      <Wrapper>
        <>
          <ArtistInfo
            avatar={pageData?.avatar || ''}
            title={pageData?.title || ''}
            description={pageData?.data.bio}
            nationality={artist?.country?.code}
            isVerified={artist?.isVerified}
          />
          {stats && stats.streams !== 0 && (
            <StreamsWrapper>
              <div className="title-wrapper">
                <h3 className="streams-number">
                  {stats.streams.toLocaleString()}
                </h3>
                {stats.status === 'equal' ? null : (
                  <div className="streams-status">
                    <StatusIcon
                      type={
                        stats.status as ComponentProps<
                          typeof StatusIcon
                        >['type']
                      }
                    />
                    <p
                      className={`streams-status-number ${stats.status}`}
                      data-status={stats.status}
                    >
                      {stats.percentageChange}
                    </p>
                  </div>
                )}
              </div>
              <p className="streams-text">Monthly Streams</p>
            </StreamsWrapper>
          )}
          {!hideIcons && (
            <SocialLinks>
              <SocialLinksContent
                className={SocialItems.length === 5 ? 'full-length' : ''}
              >
                {SocialItems.map(
                  (social, key) =>
                    social.url && (
                      <SocialLink href={social.url} key={key}>
                        {getSocialIcon(social.provider)}
                      </SocialLink>
                    )
                )}
              </SocialLinksContent>
            </SocialLinks>
          )}
        </>
        {featuredItem && (
          <SidebarHighlight>
            <SidebarFeaturedItem pageData={pageData} data={featuredItem} />
          </SidebarHighlight>
        )}
      </Wrapper>
      {isMobile && <TracpageNavbar />}
      {!isMobile && <SidebarFooter noExploreLink />}
    </SidebarWrapper>
  );
};

export default connect(mapStateToProps)(Sidebar);
